import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useFavorites } from "@/hooks/useFavorites";
import {
  categories,
  formatDuration,
  formatViews,
  getMovieById,
  getRelatedMovies,
} from "@/lib/movies";
import PlayerDemo from "@/pages/detail/components/PlayerDemo";

export default function DetailPage() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const { toggleFavorite, isFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  usePageMeta({
    title: movie
      ? `${movie.title} (${movie.year}) - 详情介绍 | 韩国伦理电影在线观看`
      : "影片未找到 | 韩国伦理电影在线观看",
    description: movie
      ? `${movie.title}（${movie.year}）是一部韩国${categories.find((item) => item.slug === movie.type)?.name}电影，由${movie.director}执导，${movie.cast.join("、")}主演。${movie.synopsis.slice(0, 70)}…`
      : "没有找到该影片，请返回片库浏览其他韩国伦理电影。",
    keywords: movie
      ? `${movie.title},${movie.originalTitle},韩国伦理电影,${movie.tags.join(",")},伦理电影在线观看`
      : "韩国伦理电影,伦理电影在线观看",
  });

  if (!movie) {
    return (
      <Layout>
        <div className="px-4 md:px-8 py-24 max-w-shell mx-auto text-center">
          <span className="w-16 h-16 rounded-full bg-secondary-100 text-secondary-900 flex items-center justify-center mx-auto">
            <i className="ri-film-line text-3xl" aria-hidden="true"></i>
          </span>
          <h1 className="font-heading text-2xl text-foreground-950 font-semibold mt-5">影片不存在或已下架</h1>
          <p className="mt-2 text-sm text-foreground-600">你访问的影片可能已调整，去片库看看其他作品吧。</p>
          <Link
            to="/category/all"
            className="mt-6 inline-flex h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm items-center gap-2 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            返回全部片库
            <i className="ri-arrow-right-line" aria-hidden="true"></i>
          </Link>
        </div>
      </Layout>
    );
  }

  const category = categories.find((item) => item.slug === movie.type);
  const related = getRelatedMovies(movie);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      await navigator.clipboard.writeText(url);
      setToast("影片链接已复制到剪贴板");
    } catch {
      setToast(`请手动复制链接：${url}`);
    }
  };

  return (
    <Layout>
      <div className="relative">
        <div className="absolute inset-0 h-[440px] md:h-[520px] overflow-hidden">
          <img
            src={movie.backdrop ?? movie.poster}
            alt={`${movie.title} 背景剧照`}
            title={`${movie.title} - 韩国伦理电影在线观看`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/85 to-background-50/50"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-background-50/80 to-transparent"></div>
        </div>

        <div className="relative px-4 md:px-8 lg:px-12 pt-7 pb-10">
          <div className="max-w-shell mx-auto">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: category?.name ?? "片库", to: `/category/${movie.type}` },
                { label: movie.title },
              ]}
            />

            <div className="mt-6 grid md:grid-cols-[190px_minmax(0,1fr)] lg:grid-cols-[240px_minmax(0,1fr)] gap-6 md:gap-9">
              <div className="w-[150px] sm:w-[180px] md:w-full shrink-0">
                <div className="rounded-lg overflow-hidden border border-background-300/50 bg-background-200 aspect-[2/3]">
                  <img
                    src={movie.poster}
                    alt={`${movie.title}（${movie.year}）电影海报`}
                    title={`${movie.title} - 韩国伦理电影在线观看`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <div className="rounded-md border border-background-200/70 bg-background-50/80 px-2 py-2 text-center">
                    <p className="font-label text-lg text-accent-500 leading-none">{movie.rating.toFixed(1)}</p>
                    <p className="mt-1 text-[10px] text-foreground-600">观众评分</p>
                  </div>
                  <div className="rounded-md border border-background-200/70 bg-background-50/80 px-2 py-2 text-center">
                    <p className="font-label text-lg text-foreground-900 leading-none">{movie.ageRating}</p>
                    <p className="mt-1 text-[10px] text-foreground-600">分级</p>
                  </div>
                </div>
              </div>

              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  <Link
                    to={`/category/${movie.type}`}
                    className="h-6 px-2.5 rounded bg-primary-500 text-background-50 text-[11px] font-medium flex items-center hover:bg-primary-600 transition-colors cursor-pointer"
                  >
                    {category?.name}
                  </Link>
                  <span className="h-6 px-2.5 rounded bg-secondary-100 text-secondary-900 text-[11px] flex items-center">
                    {movie.ageRating} 建议
                  </span>
                  {movie.featured && (
                    <span className="h-6 px-2.5 rounded bg-accent-500/20 text-accent-500 text-[11px] flex items-center">
                      焦点推荐
                    </span>
                  )}
                </div>

                <h1 className="font-heading text-2xl md:text-4xl lg:text-[42px] text-foreground-950 font-bold leading-tight">
                  {movie.title}
                </h1>
                <p className="mt-2 font-label text-sm tracking-[0.2em] text-primary-400">{movie.originalTitle}</p>

                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-foreground-600">
                  <span>{movie.year}</span>
                  <span className="w-1 h-1 rounded-full bg-background-400"></span>
                  <span>{formatDuration(movie.duration)}</span>
                  <span className="w-1 h-1 rounded-full bg-background-400"></span>
                  <span>导演 {movie.director}</span>
                  <span className="w-1 h-1 rounded-full bg-background-400"></span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-eye-line" aria-hidden="true"></i>
                    {formatViews(movie.views)} 次浏览
                  </span>
                </div>

                <p className="mt-4 text-sm md:text-[15px] text-foreground-700 leading-relaxed max-w-3xl line-clamp-3">
                  {movie.synopsis}
                </p>

                <div className="mt-5 flex flex-wrap gap-1.5">
                  {movie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="h-6 px-2 rounded bg-background-100 border border-background-200/70 text-foreground-600 text-[11px] flex items-center"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={() => document.getElementById("player")?.scrollIntoView({ behavior: "smooth", block: "center" })}
                    className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium flex items-center gap-2 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-play-fill text-lg" aria-hidden="true"></i>
                    播放演示
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const added = toggleFavorite(movie.id);
                      setToast(added ? "已加入我的片单" : "已从片单移除");
                    }}
                    className={`h-11 px-5 rounded-md border text-sm flex items-center gap-2 whitespace-nowrap transition-colors cursor-pointer ${
                      isFavorite(movie.id)
                        ? "border-accent-500 text-accent-500 bg-accent-500/10"
                        : "border-background-300 text-foreground-800 hover:border-primary-400 hover:text-primary-400"
                    }`}
                  >
                    <i
                      className={isFavorite(movie.id) ? "ri-bookmark-fill text-base" : "ri-bookmark-line text-base"}
                      aria-hidden="true"
                    ></i>
                    {isFavorite(movie.id) ? "已收藏" : "加入片单"}
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="h-11 px-5 rounded-md border border-background-300 text-foreground-800 text-sm flex items-center gap-2 hover:border-primary-400 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-share-forward-line text-base" aria-hidden="true"></i>
                    分享链接
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 md:px-8 lg:px-12 pb-14 md:pb-20">
        <div className="max-w-shell mx-auto grid lg:grid-cols-[minmax(0,1fr)_320px] gap-8 lg:gap-10">
          <div className="min-w-0">
            <section id="player" className="scroll-mt-28">
              <h2 className="font-heading text-lg md:text-xl text-foreground-950 font-semibold mb-4 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-primary-500"></span>
                在线播放演示
              </h2>
              <PlayerDemo movie={movie} />
            </section>

            <section className="mt-10">
              <h2 className="font-heading text-lg md:text-xl text-foreground-950 font-semibold mb-4 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-primary-500"></span>
                剧情简介
              </h2>
              <div className="rounded-lg border border-background-200/70 bg-background-100/40 p-5 md:p-6">
                <p className="text-sm md:text-[15px] leading-[1.9] text-foreground-700">{movie.synopsis}</p>
              </div>
            </section>

            <section className="mt-10">
              <h2 className="font-heading text-lg md:text-xl text-foreground-950 font-semibold mb-4 flex items-center gap-2">
                <span className="w-1 h-5 rounded-full bg-primary-500"></span>
                影片资料
              </h2>
              <div className="rounded-lg border border-background-200/70 bg-background-100/40 overflow-hidden">
                <dl className="grid grid-cols-1 sm:grid-cols-2">
                  {[
                    { label: "中文片名", value: movie.title },
                    { label: "韩文原名", value: movie.originalTitle },
                    { label: "导演", value: movie.director },
                    { label: "主演", value: movie.cast.join(" / ") },
                    { label: "影片类型", value: category?.name ?? "伦理电影" },
                    { label: "上映时间", value: `${movie.year} 年（${movie.releaseDate}）` },
                    { label: "片长", value: formatDuration(movie.duration) },
                    { label: "评分", value: `${movie.rating.toFixed(1)} / 10` },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-start gap-4 px-5 py-3.5 border-b border-background-200/60 sm:odd:border-r"
                    >
                      <dt className="text-xs text-foreground-500 w-20 shrink-0 pt-0.5">{item.label}</dt>
                      <dd className="text-sm text-foreground-900 leading-relaxed">{item.value}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </section>

            <section className="mt-10">
              <div className="flex items-end justify-between gap-3 mb-4">
                <h2 className="font-heading text-lg md:text-xl text-foreground-950 font-semibold flex items-center gap-2">
                  <span className="w-1 h-5 rounded-full bg-primary-500"></span>
                  相关推荐
                </h2>
                <Link
                  to={`/category/${movie.type}`}
                  className="text-xs text-primary-500 hover:text-primary-400 transition-colors flex items-center gap-1 whitespace-nowrap cursor-pointer"
                >
                  更多{category?.name}
                  <i className="ri-arrow-right-line" aria-hidden="true"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                {related.map((item) => (
                  <MovieCard key={item.id} movie={item} compact />
                ))}
              </div>
            </section>
          </div>

          <aside className="lg:sticky lg:top-24 self-start space-y-4">
            <div className="rounded-lg border border-background-200/70 bg-background-100/50 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-foreground-500">观众评分</p>
                  <p className="font-label text-4xl text-foreground-950 leading-none mt-1">
                    {movie.rating.toFixed(1)}
                  </p>
                </div>
                <div className="text-accent-500 flex items-center gap-0.5">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <i
                      key={star}
                      className={
                        movie.rating >= 8.8 || star <= 4 ? "ri-star-fill text-sm" : "ri-star-half-fill text-sm"
                      }
                      aria-hidden="true"
                    ></i>
                  ))}
                </div>
              </div>
              <div className="mt-4 h-px bg-background-200/70"></div>
              <dl className="mt-4 space-y-2.5 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500 text-xs">所属分类</dt>
                  <dd>
                    <Link
                      to={`/category/${movie.type}`}
                      className="text-foreground-900 hover:text-primary-400 transition-colors cursor-pointer"
                    >
                      {category?.name}
                    </Link>
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500 text-xs">上映年份</dt>
                  <dd className="text-foreground-900">{movie.year}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500 text-xs">片长</dt>
                  <dd className="text-foreground-900">{formatDuration(movie.duration)}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-foreground-500 text-xs">适龄提示</dt>
                  <dd className="text-foreground-900">{movie.ageRating}</dd>
                </div>
              </dl>
            </div>

            <div className="rounded-lg border border-background-200/70 bg-background-100/50 p-5">
              <h3 className="text-sm font-medium text-foreground-950 mb-3">分类标签</h3>
              <div className="flex flex-wrap gap-1.5">
                {movie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="h-7 px-2.5 rounded-full bg-secondary-100 text-secondary-900 text-xs flex items-center"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <Link
                to={`/category/${movie.type}`}
                className="mt-4 w-full h-9 rounded-md border border-background-300 text-foreground-800 text-xs flex items-center justify-center gap-1.5 hover:border-primary-400 hover:text-primary-400 transition-colors whitespace-nowrap cursor-pointer"
              >
                浏览{category?.name}全部影片
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            </div>

            <div className="rounded-lg border border-secondary-200/50 bg-secondary-100/40 p-5">
              <h3 className="text-sm font-medium text-secondary-950 mb-2">版权与内容说明</h3>
              <p className="text-xs leading-relaxed text-secondary-900">
                本站影片条目、人物与剧情均为虚构示例，仅用于展示影视资料库的信息结构。
                播放区域不提供真实片源，若需观看请前往正版授权平台。
              </p>
            </div>
          </aside>
        </div>
      </div>

      {toast && (
        <div className="fixed left-1/2 -translate-x-1/2 bottom-8 z-[70] max-w-[92vw] px-4 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm text-center break-all animate-fade-up">
          {toast}
        </div>
      )}
    </Layout>
  );
}